/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	BoxView,
	ActionButton,
	Radiobutton,
	Tooltip,
	BoxEmptyView,
	Icon,
} from '@components/atoms'
import { ListItemButton } from '@components/molecules'
import {
	AccessControlView,
	AccessGroupUserList,
	AccessGroupTab,
	AccessControlSideModal,
	AccessGroupList,
} from '@components/systems'
import { motion } from 'framer-motion'

import {
	useAppSelector,
	useAppDispatch,
	accessControlActions,
	ApiGetDataStatus,
} from '@redux/index'
import { grey, blue } from '@mui/material/colors'
import { AccessControlType, AccessGroupType } from '@schemas'

const AccessControlPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const selectedTab: string = useAppSelector(state => state.accessControl.selectedTab)
	const groupStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupAccessControlStatus,
	)
	const userStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.userAccessControlStatus,
	)
	const selectedAccessGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)
	const selectedAccessGroupByUserTab: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroupByUserTab,
	)
	const selectedGroupAccessControl: AccessControlType[] = useAppSelector(
		state => state.accessControl.selectedGroupAccessControl,
	)
	const selectedGroupAccessControlByUserTab: AccessControlType[] = useAppSelector(
		state => state.accessControl.selectedGroupAccessControlByUserTab,
	)
	const groupListByUserTab: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupListByUserTab,
	)
	const groupListStatusByUserTab: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupListStatusByUserTab,
	)

	// variables
	const groupMemberStatus: ApiGetDataStatus =
		(selectedTab === 'group' && groupStatus === 'loading') ||
		(selectedTab === 'user' && userStatus === 'loading')
			? 'loading'
			: selectedAccessGroup.users.length > 0
			? 'ok'
			: selectedAccessGroup.users.length === 0
			? 'empty'
			: groupStatus

	const onGroupByUserTabClick = (aGroup: AccessGroupType) => {
		reduxDispatch(
			accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB({
				...aGroup,
			}),
		)
	}

	const onEditGroups = () => {
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(true))
	}

	const onJoinGropClick = () => {
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(true))
	}

	return (
		<View css={style}>
			<View className="groupView">
				<AccessGroupTab />
			</View>

			<View className="accessView">
				<motion.div
					className="groupListAnimatedView"
					initial={false}
					animate={
						selectedTab === 'user' ? { opacity: 1, x: 0 } : { opacity: 0, x: '-100rem' }
					}
					exit={{ opacity: 0, x: '-100rem' }}
					transition={{ duration: 0.3 }}
				>
					<BoxView
						title="그룹 리스트"
						className="accessGroupListView"
						rightHeaderAccessory={
							<Tooltip title="그룹 수정">
								<ActionButton
									buttonColor="info"
									onClick={onEditGroups}
									iconName="cog"
									iconTextColor={grey[700]}
								/>
							</Tooltip>
						}
					>
						{groupListStatusByUserTab === 'empty' ? (
							<View className="accessGroupListEmptyView">
								<BoxEmptyView
									isGrey={true}
									title="연결된 그룹이 없습니다."
									subtitle="아래 + 버튼을 눌러서 추가해주세요."
								/>
								<View className="addButtonOuter">
									<ListItemButton
										className="listItem addItem"
										css={{ ...style }}
										onClick={onJoinGropClick}
									>
										<View className="addView">
											<Icon name="plus-thick" className="addIcon" />
										</View>
									</ListItemButton>
								</View>
							</View>
						) : (
							<AccessGroupList
								groupList={groupListByUserTab}
								status={groupListStatusByUserTab}
								onGroupClick={onGroupByUserTabClick}
								selectedGroupIds={[selectedAccessGroupByUserTab.id]}
								rightAccessory={(group, isChecked) => {
									return (
										<Radiobutton
											value={group.id}
											selectedValue={isChecked ? group.id : 'none'}
											color={blue['A400']}
										/>
									)
								}}
							/>
						)}
					</BoxView>
				</motion.div>

				<motion.div
					className="accessMenuAnimationView"
					initial={false}
					animate={selectedTab === 'group' ? { x: 0 } : { x: '20rem' }}
					exit={{ x: '100%' }}
					transition={{ duration: 0.3 }}
				>
					<BoxView
						title="메뉴 관리"
						className="accessMenuView"
						css={selectedTab === 'group' ? { paddingRight: 0 } : { paddingLeft: 0 }}
					>
						<AccessControlView
							status={selectedTab === 'group' ? groupStatus : userStatus}
							accessControlList={
								selectedTab === 'group'
									? selectedGroupAccessControl
									: selectedGroupAccessControlByUserTab
							}
							isSelectedGroup={selectedAccessGroupByUserTab.id !== ''}
							detailItemStyle={{ paddingLeft: '10rem' }}
							isShownDeleteButton={false}
						/>
					</BoxView>
				</motion.div>

				<motion.div
					className="accessGroupUserListAnimatedView"
					initial={false}
					animate={
						selectedTab === 'group' ? { opacity: 1, x: 0 } : { opacity: 0, x: '100rem' }
					}
					exit={{ opacity: 0, right: -200 }}
					transition={{ duration: 0.3 }}
				>
					<BoxView className="accessGroupUserListView" title="그룹 멤버 관리">
						<AccessGroupUserList
							status={groupMemberStatus}
							userList={selectedAccessGroup.users}
						/>
					</BoxView>
				</motion.div>
			</View>

			<AccessControlSideModal />
		</View>
	)
}

export default AccessControlPage

const style = css({
	position: 'relative',
	display: 'flex',

	'.groupView': {
		width: '25rem',
		marginRight: '0.5rem',
	},

	'.accessView': {
		position: 'relative',
		display: 'flex',
		width: 'calc(100% - 20.5rem)',
		height: 'fit-contents',
		background: '#fff',
		borderRadius: '5px',
		overflow: 'hidden',

		'.groupListAnimatedView': {
			position: 'absolute',
			width: '20rem',

			'.accessGroupListView': {
				width: '20rem',

				'.boxChildrenView': {
					position: 'relative',
					overflow: 'hidden',

					'.accessGroupListEmptyView': {
						width: '100%',
						height: '100%',
						border: '1px solid',
						borderColor: grey[200],
						borderRadius: '5px',

						'.addButtonOuter': {
							position: 'absolute',
							width: '100%',
							height: '100%',
							top: 0,
							padding: '0 1rem',

							'.listItem': {
								position: 'absolute',
								width: 'calc(100% - 2rem)',
								bottom: '3rem',
								marginBottom: '0.2rem',
								background: '#FFF',
								borderRadius: '5px',
								border: '1px solid',
								borderColor: grey[300],

								'&.addItem': {
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									background: grey[200],
									border: 0,

									'.addView': {
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',

										'.addIcon': {
											color: grey[600],
										},
									},
								},
							},
						},
					},
				},
			},
		},

		'.accessMenuAnimationView': {
			position: 'relative',
			width: 'calc(100% - 20rem)',
			maxWidth: '36rem',

			'.accessMenuView': {
				width: '100%',
			},
		},

		'.accessGroupUserListAnimatedView': {
			position: 'relative',
			width: '20rem',

			'.accessGroupUserListView': {
				width: '100%',
			},
		},
	},

	'.accessControlView': {
		width: 'calc(100% - 20.5rem)',
		height: 'fit-contents',

		'.accessControlInnerView': {
			position: 'relative',
			display: 'flex',
			width: '100%',
			height: '100%',
		},
	},
})
